/**
 * MIT License
 * Copyright (c) 2023 Jamasp Team
 */
import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../auth/AuthContext';
import PropTypes from 'prop-types';
import '../../pages/Home.css';

const styles = {
  nav: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    background: '#fff',
    borderBottom: '1px solid #D6E2EB',
    height: 56,
    display: 'flex',
    alignItems: 'center',
    padding: '0 32px',
    justifyContent: 'space-between',
    fontFamily: "'DM Sans', sans-serif",
  },
  logo: {
    fontFamily: "'DM Mono', monospace",
    fontSize: 15,
    fontWeight: 500,
    color: '#131B20',
    textDecoration: 'none',
    letterSpacing: '-0.5px',
  },
  logoAccent: {
    color: '#0A66C2',
  },
  links: {
    display: 'flex',
    alignItems: 'center',
    gap: 28,
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  link: {
    fontSize: 14,
    color: '#4C5967',
    textDecoration: 'none',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    fontFamily: "'DM Sans', sans-serif",
    padding: 0,
  },
  avatar: {
    width: 30,
    height: 30,
    borderRadius: '50%',
    background: '#0A66C2',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    fontFamily: "'DM Sans', sans-serif",
  },
  dropdown: {
    position: 'absolute',
    top: 48,
    right: 32,
    background: '#fff',
    border: '1px solid #D6E2EB',
    borderRadius: 8,
    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
    minWidth: 160,
    zIndex: 200,
    overflow: 'hidden',
  },
  dropdownItem: {
    display: 'block',
    width: '100%',
    padding: '10px 16px',
    fontSize: 14,
    color: '#4C5967',
    textDecoration: 'none',
    background: 'none',
    border: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    fontFamily: "'DM Sans', sans-serif",
  },
};

function Navbar({ role: _role }) {  // eslint-disable-line no-unused-vars
  const { currentUser, userJamasp, logout, hasRole } = useAuthContext();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef(null);
  const navigate = useNavigate();

  const isAdmin = hasRole && hasRole('admin');

  // Close dropdown on outside click
  React.useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = () => {
    setMenuOpen(false);
    logout().then(() => navigate('/', { replace: true }));
  };

  const initials = currentUser?.displayName
    ? currentUser.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  return (
    <nav style={styles.nav}>
      {/* Logo */}
      <Link to="/home" style={styles.logo}>
        jamas<span style={styles.logoAccent}>p</span>.
      </Link>

      {/* Nav links + actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
        <Link to="/about" style={styles.link}>About</Link>
        <a
          href="https://github.com/cutsocial/jamasp-public"
          target="_blank"
          rel="noreferrer"
          style={styles.link}
        >
          GitHub
        </a>

        {/* Logged in — show avatar + dropdown */}
        {userJamasp ? (
          <div style={{ position: 'relative' }} ref={menuRef}>
            <button
              style={styles.avatar}
              onClick={() => setMenuOpen(o => !o)}
              aria-label="User menu"
            >
              {initials}
            </button>
            {menuOpen && (
              <div style={styles.dropdown}>
                {isAdmin ? (
                  <Link
                    to="/admin"
                    style={styles.dropdownItem}
                    onClick={() => setMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                ) : (
                  <Link
                    to="/user/home"
                    style={styles.dropdownItem}
                    onClick={() => setMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                )}
                <Link
                  to="/user/profile"
                  style={styles.dropdownItem}
                  onClick={() => setMenuOpen(false)}
                >
                  Profile
                </Link>
                <button
                  style={{ ...styles.dropdownItem, color: '#c03030' }}
                  onClick={handleLogout}
                >
                  Log out
                </button>
              </div>
            )}
          </div>
        ) : (
          /* Not logged in — show plain login link */
          <Link to="/auth/login" style={styles.link}>
            Login
          </Link>
        )}
      </div>
    </nav>
  );
}

Navbar.propTypes = {
  role: PropTypes.string,
};

export default Navbar;